/**
 * MUSKLE product reviews — live App Proxy (Phase 2C write + Phase 2E/2F read).
 *
 * Customer-state rules:
 * 1. Public reviews load for everyone.
 * 2. Any visitor can open and submit the review form. Login is not required.
 * 3. New reviews stay pending until MUSKLE approves them.
 * 4. Verified buyer badge only comes from an approved review with verified_purchase.
 */
(function () {
  'use strict';

  var PROXY_BASE = '/apps/muskle-reviews';

  function createState(productId) {
    return {
      productId: String(productId || ''),
      loading: true,
      authenticated: false,
      eligible: false,
      verifiedPurchase: false,
      reason: null,
      submitting: false,
      submitted: false,
      publicLoaded: false,
      publicData: null,
    };
  }

  function getState(root) {
    return root.__muskleReviewsState || null;
  }

  function isEnglish() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return lang.indexOf('en') === 0;
  }

  function copy() {
    if (isEnglish()) {
      return {
        writeReview: 'Write a review',
        emptyTitle: 'No reviews yet',
        emptySupport: 'Share your experience with this product with other customers.',
        pending: 'Thank you. Your review has been received and is being reviewed by MUSKLE.',
        alreadyReviewed: 'You have already reviewed this product.',
        submit: 'Submit review',
        submitting: 'Submitting…',
        errorGeneric: 'Something went wrong. Please try again later.',
        errorValidation: 'Please check your rating, name, email, title and review text.',
        ratingAria: 'Choose a rating',
        reviewsCount: function (n) {
          return n === 1 ? '1 review' : n + ' reviews';
        },
        scoreOutOf: function (score) {
          return score + ' out of 5';
        },
        star: function (n) {
          return n + ' star' + (n === 1 ? '' : 's');
        },
        barLabel: function (n) {
          return n + ' star' + (n === 1 ? '' : 's');
        },
        translatedFromDutch: 'Translated from Dutch',
        translatedFromEnglish: 'Translated from English',
        showOriginal: 'Show original',
        showTranslation: 'Show translation',
      };
    }
    return {
      writeReview: 'Schrijf een beoordeling',
      emptyTitle: 'Nog geen beoordelingen',
      emptySupport: 'Deel je ervaring met dit product met andere klanten.',
      pending: 'Bedankt. Je beoordeling is ontvangen en wordt gecontroleerd door MUSKLE.',
      alreadyReviewed: 'Je hebt dit product al beoordeeld.',
      submit: 'Beoordeling versturen',
      submitting: 'Bezig met versturen…',
      errorGeneric: 'Er ging iets mis. Probeer het later opnieuw.',
      errorValidation: 'Controleer je score, naam, e-mail, titel en reviewtekst.',
      ratingAria: 'Kies een score',
      reviewsCount: function (n) {
        return n === 1 ? '1 beoordeling' : n + ' beoordelingen';
      },
      scoreOutOf: function (score) {
        return score + ' uit 5';
      },
      star: function (n) {
        return n + ' ster' + (n === 1 ? '' : 'ren');
      },
      barLabel: function (n) {
        return n + ' ster' + (n === 1 ? '' : 'ren');
      },
      translatedFromDutch: 'Vertaald uit het Nederlands',
      translatedFromEnglish: 'Vertaald uit het Engels',
      showOriginal: 'Toon origineel',
      showTranslation: 'Toon vertaling',
    };
  }

  function proxyFetch(path, init) {
    return fetch(PROXY_BASE + path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(init && init.headers ? init.headers : {}) },
      ...(init || {}),
    }).then(function (res) {
      return res.json().catch(function () {
        return { ok: false, _httpStatus: res.status };
      }).then(function (body) {
        body._httpStatus = res.status;
        return body;
      });
    });
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  function setText(el, text) {
    if (el) el.textContent = text;
  }

  function formatScoreNumber(value) {
    var num = Number(value);
    if (!Number.isFinite(num)) return '';
    var fixed = (Math.round(num * 10) / 10).toFixed(1);
    return isEnglish() ? fixed : fixed.replace('.', ',');
  }

  function formatReviewDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    var day = String(d.getDate()).padStart(2, '0');
    var month = String(d.getMonth() + 1).padStart(2, '0');
    var year = String(d.getFullYear()).slice(-2);
    return day + '/' + month + '/' + year;
  }

  function emptyPublicData() {
    return {
      ok: true,
      aggregate: { review_count: 0, average_rating: null, rating_distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } },
      reviews: [],
    };
  }

  function reviewCount(payload) {
    var aggregate = payload && payload.aggregate ? payload.aggregate : null;
    var count = aggregate ? Number(aggregate.review_count) : 0;
    return Number.isFinite(count) && count > 0 ? count : 0;
  }

  function setReviewsLayoutState(root, count) {
    var liveRoot = root.querySelector('.muskle-reviews-live') || root;
    liveRoot.classList.toggle('muskle-reviews-live--has-reviews', count > 0);
    liveRoot.classList.toggle('muskle-reviews-live--empty', count === 0);
  }

  function clearCustomerEligibility(state) {
    state.eligible = false;
    state.verifiedPurchase = false;
    state.reason = null;
    state.submitted = false;
  }

  function isAuthenticatedIdentity(identity) {
    return Boolean(
      identity &&
        identity.ok !== false &&
        (identity.authenticated === true || identity.has_logged_in_customer_id === true)
    );
  }

  function applyEligibility(state, eligibility, expectedProductId) {
    if (!state.authenticated) {
      clearCustomerEligibility(state);
      return;
    }
    if (!eligibility || eligibility.ok === false) {
      clearCustomerEligibility(state);
      return;
    }
    if (eligibility.error === 'login_required') {
      state.authenticated = false;
      clearCustomerEligibility(state);
      return;
    }
    if (expectedProductId && state.productId && String(expectedProductId) !== String(state.productId)) {
      return;
    }
    state.eligible = Boolean(eligibility.eligible);
    state.verifiedPurchase = Boolean(eligibility.verified_purchase);
    state.reason = eligibility.reason || null;
  }

  function renderDisplayStars(container, rating) {
    if (!container) return;
    container.innerHTML = '';
    container.setAttribute('role', 'img');
    var value = Math.max(0, Math.min(5, Number(rating) || 0));
    for (var i = 1; i <= 5; i += 1) {
      var span = document.createElement('span');
      span.className = 'muskle-reviews-live__star';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = '★';
      if (value >= i) span.classList.add('is-full');
      else if (value >= i - 0.5) span.classList.add('is-partial');
      container.appendChild(span);
    }
    container.setAttribute('aria-label', copy().star(Math.max(0, Math.round(value))));
  }

  function renderInteractiveStars(container, rating, onPick) {
    if (!container) return;
    container.innerHTML = '';
    container.setAttribute('role', 'radiogroup');
    container.setAttribute('aria-label', copy().ratingAria);
    var value = Number(rating) || 0;
    for (var i = 1; i <= 5; i += 1) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'muskle-reviews-live__star-btn' + (i <= value ? ' is-active' : '');
      btn.textContent = '★';
      btn.setAttribute('aria-label', copy().star(i));
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', i === value ? 'true' : 'false');
      btn.dataset.rating = String(i);
      btn.addEventListener('click', function () {
        onPick(Number(this.dataset.rating));
      });
      container.appendChild(btn);
    }
  }

  function renderDistributionBars(root, distribution, total) {
    var barsEl = root.querySelector('[data-muskle-reviews-bars]');
    if (!barsEl) return;
    if (!total || !distribution) {
      setHidden(barsEl, true);
      barsEl.innerHTML = '';
      return;
    }
    setHidden(barsEl, false);
    barsEl.innerHTML = '';
    for (var star = 5; star >= 1; star -= 1) {
      var count = Number(distribution[star]) || 0;
      var pct = total ? Math.round((count / total) * 100) : 0;
      var row = document.createElement('div');
      row.className = 'muskle-reviews-live__bar-row';
      row.setAttribute('role', 'listitem');

      var label = document.createElement('span');
      label.textContent = copy().barLabel(star);
      var track = document.createElement('div');
      track.className = 'muskle-reviews-live__bar-track';
      var fill = document.createElement('span');
      fill.className = 'muskle-reviews-live__bar-fill';
      fill.style.width = pct + '%';
      track.appendChild(fill);
      var pctEl = document.createElement('span');
      pctEl.className = 'muskle-reviews-live__bar-pct';
      pctEl.textContent = pct + '%';

      row.appendChild(label);
      row.appendChild(track);
      row.appendChild(pctEl);
      barsEl.appendChild(row);
    }
  }

  function storefrontLocale() {
    return isEnglish() ? 'en' : 'nl';
  }

  function translationNoticeLabel(review) {
    var c = copy();
    if (!review || !review.is_translated) return '';
    if (review.original_locale === 'nl') return c.translatedFromDutch;
    if (review.original_locale === 'en') return c.translatedFromEnglish;
    return c.translatedFromDutch;
  }

  function applyCardDisplay(li, review, showingOriginal) {
    var titleEl = li.querySelector('[data-review-title]');
    var bodyEl = li.querySelector('[data-review-body]');
    var noticeEl = li.querySelector('[data-review-translation-notice]');
    var toggleBtn = li.querySelector('[data-review-toggle-original]');
    if (!titleEl || !bodyEl) return;

    var title = review.title;
    var body = review.body;
    if (review.is_translated && showingOriginal) {
      title = review.original_title || review.title;
      body = review.original_body || review.body;
    }
    titleEl.textContent = title;
    bodyEl.textContent = body;

    if (noticeEl && toggleBtn) {
      if (review.is_translated) {
        var prefix = noticeEl.querySelector('[data-review-notice-prefix]');
        if (prefix) setHidden(prefix, showingOriginal);
        setHidden(noticeEl, false);
        toggleBtn.textContent = showingOriginal ? copy().showTranslation : copy().showOriginal;
        setHidden(toggleBtn, false);
      } else {
        setHidden(noticeEl, true);
        setHidden(toggleBtn, true);
      }
    }
  }

  function renderReviewCards(root, reviews) {
    var listEl = root.querySelector('[data-muskle-reviews-list]');
    var verifiedLabel = root.dataset.verifiedBuyerLabel || (isEnglish() ? 'Verified buyer' : 'Geverifieerde koper');
    if (!listEl) return;
    listEl.innerHTML = '';

    reviews.forEach(function (review) {
      var li = document.createElement('li');
      li.className = 'muskle-reviews-live__card';
      li.dataset.showingOriginal = 'false';

      var top = document.createElement('div');
      top.className = 'muskle-reviews-live__card-top';
      var stars = document.createElement('div');
      stars.className = 'muskle-reviews-live__stars muskle-reviews-live__stars--card muskle-reviews-live__card-stars';
      renderDisplayStars(stars, review.rating);
      top.appendChild(stars);
      li.appendChild(top);

      var title = document.createElement('h3');
      title.className = 'muskle-reviews-live__card-title';
      title.dataset.reviewTitle = 'true';
      li.appendChild(title);

      var body = document.createElement('p');
      body.className = 'muskle-reviews-live__card-body';
      body.dataset.reviewBody = 'true';
      li.appendChild(body);

      if (review.is_translated) {
        var notice = document.createElement('p');
        notice.className = 'muskle-reviews-live__translation-notice';
        notice.dataset.reviewTranslationNotice = 'true';
        var noticeText = document.createElement('span');
        noticeText.dataset.reviewNoticePrefix = 'true';
        noticeText.textContent = translationNoticeLabel(review) + ' · ';
        notice.appendChild(noticeText);
        var toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'muskle-reviews-live__translation-toggle';
        toggleBtn.dataset.reviewToggleOriginal = 'true';
        toggleBtn.textContent = copy().showOriginal;
        toggleBtn.addEventListener('click', function () {
          var showing = li.dataset.showingOriginal === 'true';
          li.dataset.showingOriginal = showing ? 'false' : 'true';
          applyCardDisplay(li, review, li.dataset.showingOriginal === 'true');
        });
        notice.appendChild(toggleBtn);
        li.appendChild(notice);
      }

      applyCardDisplay(li, review, false);

      var meta = document.createElement('div');
      meta.className = 'muskle-reviews-live__card-meta';

      var name = document.createElement('span');
      name.className = 'muskle-reviews-live__card-name';
      name.textContent = review.display_name || '';
      meta.appendChild(name);

      if (review.verified_purchase) {
        var verified = document.createElement('span');
        verified.className = 'muskle-reviews-live__verified';
        var icon = document.createElement('span');
        icon.className = 'muskle-reviews-live__verified-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '✓';
        verified.appendChild(icon);
        verified.appendChild(document.createTextNode(' ' + verifiedLabel));
        meta.appendChild(verified);
      }

      if (review.submitted_at) {
        var time = document.createElement('time');
        time.setAttribute('datetime', review.submitted_at);
        time.textContent = formatReviewDate(review.submitted_at);
        meta.appendChild(time);
      }

      li.appendChild(meta);
      listEl.appendChild(li);
    });
  }

  function renderPublicReviews(root, data) {
    var state = getState(root);
    if (!state) return;

    var payload = data && data.ok ? data : emptyPublicData();
    state.publicData = payload;
    state.publicLoaded = true;

    var count = reviewCount(payload);
    var average = payload.aggregate ? payload.aggregate.average_rating : null;
    var reviews = Array.isArray(payload.reviews) ? payload.reviews : [];
    var c = copy();
    var displayRating = count > 0 && average != null ? average : 0;

    var headlineStars = root.querySelector('[data-muskle-reviews-headline-stars]');
    var headlineText = root.querySelector('[data-muskle-reviews-headline-text]');
    var emptySupport = root.querySelector('[data-muskle-reviews-empty-support]');
    var summaryActive = root.querySelector('[data-muskle-reviews-summary-active]');
    var summaryScore = root.querySelector('[data-muskle-reviews-summary-score]');
    var summaryStars = root.querySelector('[data-muskle-reviews-summary-stars]');
    var summaryCount = root.querySelector('[data-muskle-reviews-summary-count]');

    renderDisplayStars(headlineStars, displayRating);
    if (count > 0 && average != null) {
      setText(headlineText, c.scoreOutOf(formatScoreNumber(average)) + ' · ' + c.reviewsCount(count));
    } else {
      setText(headlineText, c.emptyTitle);
    }

    setReviewsLayoutState(root, count);
    // Single empty-support line only — never a second "Nog geen beoordelingen" band.
    setHidden(emptySupport, count > 0);
    if (emptySupport && count === 0) setText(emptySupport, c.emptySupport);

    if (count > 0) {
      setHidden(summaryActive, false);
      setText(summaryScore, formatScoreNumber(average));
      renderDisplayStars(summaryStars, average);
      setText(summaryCount, c.reviewsCount(count));
      renderDistributionBars(root, payload.aggregate.rating_distribution, count);
      renderReviewCards(root, reviews);
    } else {
      setHidden(summaryActive, true);
      setText(summaryScore, '');
      if (summaryStars) summaryStars.innerHTML = '';
      setText(summaryCount, '');
      renderDistributionBars(root, null, 0);
      renderReviewCards(root, []);
    }

    render(root);
  }

  /**
   * Deterministic customer-state machine.
   * Auth gate is first — eligibility reasons are ignored when logged out.
   */
  function resolveUiState(state) {
    var c = copy();
    if (state.reason === 'review_pending' || state.submitted) {
      return { key: 'pending', message: c.pending };
    }
    if (state.reason === 'review_already_exists') {
      return { key: 'already_reviewed', message: c.alreadyReviewed };
    }
    return { key: 'can_write', showWrite: true };
  }

  function render(root) {
    var state = getState(root);
    if (!state) return;

    var ui = resolveUiState(state);
    var c = copy();

    var statusEl = root.querySelector('[data-muskle-reviews-status]');
    var successEl = root.querySelector('[data-muskle-reviews-success]');
    var loginBtn = root.querySelector('[data-muskle-reviews-login-btn]');
    var writeBtn = root.querySelector('[data-muskle-reviews-write-btn]');
    var ctaNote = root.querySelector('[data-muskle-reviews-cta-note]');
    var ctaSlot = root.querySelector('[data-muskle-reviews-cta-slot]');
    var formPanel = root.querySelector('[data-muskle-reviews-form-panel]');

    var hideCta = ui.key === 'pending' || ui.key === 'already_reviewed';
    var showWrite = Boolean(ui.showWrite && !hideCta);
    var showPending = ui.key === 'pending';
    var showAlready = ui.key === 'already_reviewed';

    setHidden(ctaSlot, !showWrite);
    setHidden(loginBtn, true);
    setHidden(writeBtn, !showWrite);
    if (writeBtn) writeBtn.textContent = c.writeReview;
    setHidden(successEl, !showPending);
    if (successEl && showPending) setText(successEl, c.pending);
    var formOpen = formPanel && !formPanel.hasAttribute('hidden');
    setHidden(formPanel, showPending || showAlready || !formOpen);
    setHidden(ctaNote, true);

    if (statusEl) {
      statusEl.textContent = showAlready ? c.alreadyReviewed : '';
      statusEl.classList.toggle('is-muted', false);
      setHidden(statusEl, !showAlready);
    }

    if (writeBtn) writeBtn.disabled = state.submitting;
  }

  function openForm(root) {
    var panel = root.querySelector('[data-muskle-reviews-form-panel]');
    if (!panel) return;
    setHidden(panel, false);
    var writeBtn = root.querySelector('[data-muskle-reviews-write-btn]');
    if (writeBtn) writeBtn.setAttribute('aria-expanded', 'true');
    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function wireForm(root) {
    var state = getState(root);
    if (!state) return;
    var form = root.querySelector('[data-muskle-reviews-form]');
    var stars = root.querySelector('[data-muskle-reviews-form-stars]');
    var ratingInput = root.querySelector('[data-muskle-reviews-rating-input]');
    var submitBtn = root.querySelector('[data-muskle-reviews-submit]');
    var errorEl = root.querySelector('[data-muskle-reviews-form-error]');
    if (!form || !stars || !ratingInput) return;

    function pickRating(rating) {
      ratingInput.value = String(rating);
      renderInteractiveStars(stars, rating, pickRating);
    }
    renderInteractiveStars(stars, 0, pickRating);

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (state.submitting) return;
      var c = copy();
      var titleInput = form.querySelector('[name="title"]');
      var bodyInput = form.querySelector('[name="body"]');
      var nameInput = form.querySelector('[name="display_name"]');
      var emailInput = form.querySelector('[name="email"]');
      var payload = {
        product_id: state.productId,
        rating: Number(ratingInput.value || 0),
        title: String(titleInput && titleInput.value ? titleInput.value : '').trim(),
        body: String(bodyInput && bodyInput.value ? bodyInput.value : '').trim(),
        display_name: String(nameInput && nameInput.value ? nameInput.value : '').trim(),
        email: String(emailInput && emailInput.value ? emailInput.value : '').trim(),
        locale: isEnglish() ? 'en' : 'nl',
      };
      if (!payload.rating || !payload.display_name || !payload.email || !payload.title || !payload.body) {
        if (errorEl) errorEl.textContent = c.errorValidation;
        return;
      }

      state.submitting = true;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = c.submitting;
      }
      if (errorEl) errorEl.textContent = '';

      proxyFetch('/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (body) {
          if (body.success || (body.ok && body.status === 'pending')) {
            state.submitted = true;
            state.reason = 'review_pending';
            setHidden(root.querySelector('[data-muskle-reviews-form-panel]'), true);
            render(root);
            return;
          }
          var msg = c.errorGeneric;
          if (body.error === 'validation_failed') msg = c.errorValidation;
          if (body.error === 'already_reviewed') {
            state.reason = 'review_already_exists';
            render(root);
            return;
          }
          if (errorEl) errorEl.textContent = msg;
        })
        .catch(function () {
          if (errorEl) errorEl.textContent = copy().errorGeneric;
        })
        .finally(function () {
          state.submitting = false;
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = copy().submit;
          }
        });
    });
  }

  function wireWriteButtons(root) {
    var writeBtn = root.querySelector('[data-muskle-reviews-write-btn]');
    if (!writeBtn) return;
    writeBtn.addEventListener('click', function () {
      openForm(root);
    });
  }

  function bootstrap(root) {
    var productId = root.dataset.productId;
    if (!productId) return;

    // Fresh per-PDP state — never reuse global eligibility/reason across products.
    var state = createState(productId);
    root.__muskleReviewsState = state;

    wireWriteButtons(root);
    wireForm(root);

    renderDisplayStars(root.querySelector('[data-muskle-reviews-headline-stars]'), 0);
    render(root);

    proxyFetch('/reviews?product_id=' + encodeURIComponent(productId) + '&locale=' + encodeURIComponent(storefrontLocale()))
      .then(function (data) {
        if (getState(root) !== state) return;
        state.loading = false;
        renderPublicReviews(root, data);
      })
      .catch(function () {
        if (getState(root) !== state) return;
        renderPublicReviews(root, emptyPublicData());
      });

    proxyFetch('/identity')
      .then(function (identity) {
        if (getState(root) !== state) return null;

        state.authenticated = isAuthenticatedIdentity(identity);
        state.loading = false;

        if (!state.authenticated) {
          clearCustomerEligibility(state);
          render(root);
          return null;
        }

        // STEP 3 — eligibility only after authenticated, scoped to this product_id.
        return proxyFetch('/eligibility?product_id=' + encodeURIComponent(state.productId)).then(function (eligibility) {
          return { eligibility: eligibility, productId: state.productId };
        });
      })
      .then(function (result) {
        if (!result) return;
        if (getState(root) !== state) return;
        applyEligibility(state, result.eligibility, result.productId);
        render(root);
      })
      .catch(function () {
        if (getState(root) !== state) return;
        state.loading = false;
        if (!state.authenticated) clearCustomerEligibility(state);
        render(root);
      });
  }

  function init() {
    document.querySelectorAll('[data-muskle-reviews-live]').forEach(bootstrap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
